import { Badge, HStack } from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'
import {
  PAYMENT_TYPE,
  PAYMENT_TYPE_BADGE,
  PAYMENT_TYPE_COLOR,
  PAYMENT_TYPE_ICON,
  PAYMENT_TYPE_LABEL,
  PAYMENT_TYPE_SOURCE_LABEL,
  PAYMENT_STATE_COLOR,
  PAYMENT_STATE_LABEL,
} from 'shared/constants/payments'
import { getDateTimeReadFormat } from 'shared/utils/dateFormatting'
import { ItemBadge, ItemIcon } from 'uikit/Snippets'
import { EmptyCell } from 'uikit/ReactTable/cells/EmptyCell'
import { rbNullDistinationTitle } from '../Payments.constants'

type Payment = {
  id: string
  paymentType: keyof typeof PAYMENT_TYPE
  state: string
  paymentDate: string
  amount: number
  currency: string
  commission: number
  commissionCurrency: string
  destinationAmount: number
  destinationCurrency: string
  payeeFIO: string
  paymentDestination: string
  payeePhone: string
  payerPhone: string
  paymentSourceType: string
  paymentSource: string
}

const columnHelper = createColumnHelper<Payment>()

export const columns = [
  columnHelper.accessor('paymentType', {
    id: 'icon',
    header: '',
    maxSize: 80,
    minSize: 60,
    enableHiding: false,
    cell: ({ row }) => (
      <ItemIcon
        pl="10px"
        IconComponent={PAYMENT_TYPE_ICON[row.original.paymentType]}
        color={PAYMENT_TYPE_COLOR[row.original.paymentType]}
        badge={PAYMENT_TYPE_BADGE[row.original.paymentType]}
      />
    ),
  }),
  columnHelper.accessor('id', {
    header: 'PaymentID',
    minSize: 180,
    maxSize: 220,
    cell: (info) => info.getValue() || <EmptyCell />,
  }),
  columnHelper.accessor('paymentType', {
    header: 'Тип',
    minSize: 190,
    maxSize: 240,
    cell: ({ row }) => {
      return PAYMENT_TYPE_LABEL[row.original.paymentType] || <EmptyCell />
    },
  }),
  columnHelper.accessor('state', {
    header: 'Статус',
    minSize: 220,
    maxSize: 250,
    cell: (info) => {
      const value = info.getValue() as keyof typeof PAYMENT_STATE_COLOR
      return (
        <ItemBadge colorPalette={PAYMENT_STATE_COLOR[value] || 'gray'} whiteSpace="wrap">
          {PAYMENT_STATE_LABEL[value] || value}
        </ItemBadge>
      )
    },
  }),
  columnHelper.accessor('paymentDate', {
    header: 'Дата',
    minSize: 190,
    maxSize: 240,
    cell: (info) => getDateTimeReadFormat(info.getValue()) || <EmptyCell />,
    enableSorting: true,
  }),
  columnHelper.accessor('amount', {
    header: 'Сумма',
    minSize: 150,
    maxSize: 200,
    cell: (info) => {
      const value = info.getValue()
      if (!value) return <EmptyCell />

      return (
        <HStack>
          <b>{value}</b>
          <Badge fontSize="0.7rem" fontWeight="500" verticalAlign="middle" colorPalette="gray">
            {info.row.original.currency}
          </Badge>
        </HStack>
      )
    },
  }),
  columnHelper.accessor('commission', {
    header: 'Сумма комиссии',
    minSize: 150,
    maxSize: 200,
    cell: (info) => {
      const value = info.getValue()
      if (!value) return <EmptyCell />

      return (
        <HStack>
          <b>{value}</b>
          <Badge fontSize="0.7rem" fontWeight="500" verticalAlign="middle" colorPalette="gray">
            {info.row.original.commissionCurrency}
          </Badge>
        </HStack>
      )
    },
  }),
  columnHelper.accessor('destinationAmount', {
    header: 'Сумма к выдаче получателю',
    minSize: 150,
    maxSize: 200,
    cell: (info) => {
      const value = info.getValue()

      if (!value && info.row.original.paymentType !== PAYMENT_TYPE.RF_RB_TRANSFER_TO_CARD)
        return <EmptyCell />
      if (value === 0 && info.row.original.paymentType === PAYMENT_TYPE.RF_RB_TRANSFER_TO_CARD) {
        return rbNullDistinationTitle
      }
      return (
        <HStack>
          <b>{value}</b>
          <Badge fontSize="0.7rem" fontWeight="500" verticalAlign="middle" colorPalette="gray">
            {info.row.original.destinationCurrency}
          </Badge>
        </HStack>
      )
    },
  }),
  columnHelper.accessor('payeeFIO', {
    header: 'ФИО получателя',
    minSize: 180,
    maxSize: 230,
    cell: (info) => info.getValue() || <EmptyCell />,
  }),
  columnHelper.accessor('paymentDestination', {
    header: 'Карта',
    minSize: 180,
    maxSize: 230,
    cell: (info) => info.getValue() || <EmptyCell />,
  }),
  columnHelper.accessor('payeePhone', {
    header: 'Получатель',
    minSize: 150,
    maxSize: 200,
    cell: (info) => info.getValue() || <EmptyCell />,
  }),
  columnHelper.accessor('payerPhone', {
    header: 'Отправитель',
    minSize: 180,
    maxSize: 230,
    cell: (info) => info.getValue() || <EmptyCell />,
  }),
  columnHelper.accessor('paymentSourceType', {
    header: 'Тип источника',
    minSize: 150,
    maxSize: 200,
    cell: (info) => {
      const value = info.getValue() as keyof typeof PAYMENT_TYPE_SOURCE_LABEL
      if (!value) return <EmptyCell />
      return PAYMENT_TYPE_SOURCE_LABEL[value]
    },
  }),
  columnHelper.accessor('paymentSource', {
    header: 'Источник',
    minSize: 200,
    maxSize: 250,
    cell: (info) => {
      const value = info.getValue()
      if (!value && info.row.original.paymentType !== 'WITHDRAW_TO_CARD') return <EmptyCell />
      return value || 'Внутренний счёт'
    },
  }),
]
